import { formatInstant } from '@/shared/geo/calendar';
import { useSyncExternalStore, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Tile } from '@/shared/components/Tile';
import { AmassaMark } from '@/shared/brand/AmassaMark';
import { iconForKey } from '@/shared/icons';
import { useModuleRegistries } from '@/shared/modules/store';
import type { HubTile } from '@/shared/modules/registries';
import { useAuth } from '@/auth/AuthContext';
import { canAccessAppraisals } from '@/auth/visibility';
import { apiFetch, ApiError } from '@/auth/api';
import { GettingStartedCard } from '@/features/onboarding/GettingStartedCard';
import { useOnboardingMe } from '@/features/onboarding/OnboardingHost';

/* Phone width, for the new interface only (see `hideKnot`). */
const NARROW_QUERY = '(max-width: 640px)';
function subscribeNarrow(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia(NARROW_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
function readNarrow(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(NARROW_QUERY).matches;
}

/*
 * Two thin fetch helpers inlined here on purpose — pulling them from
 * `coportal.api.ts` (1.8k LoC) into the eagerly-imported dashboard
 * chunk balloons the main bundle. The Dashboard is loaded on every
 * route via the shell, so it must stay slim.
 */

interface RawSummary {
  IssuesOpen?: number;
  issuesOpen?: number;
}

async function fetchOpenIssues(signal: AbortSignal): Promise<number | null> {
  try {
    const raw = await apiFetch<RawSummary>('/coportal/dashboard/summary', {
      method: 'GET',
      signal,
    });
    // Handle both PascalCase and camelCase wire shapes.
    return raw.IssuesOpen ?? raw.issuesOpen ?? null;
  } catch (err) {
    if (err instanceof ApiError) return null;
    return null;
  }
}

interface AgentRow {
  id?: string;
}

async function fetchAgentsCount(signal: AbortSignal): Promise<number | null> {
  try {
    const rows = await apiFetch<AgentRow[]>('/coportal/agents', {
      method: 'GET',
      signal,
    });
    return Array.isArray(rows) ? rows.length : null;
  } catch (err) {
    if (err instanceof ApiError) return null;
    return null;
  }
}

/**
 * Dashboard — Quotinc Aura redesign.
 *
 * Above the grid: `.hub-hero` panel with a cobalt/sienna aurora, grid
 * overlay and drifting orb (heroOrbDrift, 14 s loop). Title is Mona Sans
 * 56 px with the italic "Hub" name in a cobalt→sienna gradient. The tile
 * grid staggers in (motion.css `.motion-stagger`).
 *
 * Tile grid: 15 canonical modules, then Employee Hub; visible set per user
 * is computed via `canSeeTile` (TS port of PHP `hubVisibleTiles()`). The
 * hero counter shows the VISIBLE module count (Employee Hub not counted) so
 * a viewer sees "2 modules" while a fully-tooled admin sees "14" (+1 if AI
 * AI AI is granted = 15).
 */

// The tiles, in Hub order: the canonical PHP tile registry
// (`hub/includes/tiles.php`), now DERIVED from `shared/modules/catalog.json`
// (each module's `label`, `hint`, `ink`, `icon`). `slug` is the canonical
// tile slug (`cs-portal`, `ai-ai-ai`) so it matches the `allowed_tools`
// claim keys directly. Shell modules draw their `ModuleIcon`; a standalone
// surface (Employee Hub, which renders outside the Shell) its catalog icon.
// The tenant's tree decides which tiles exist and what they are called
// (Admin ▸ Modules): `useModuleRegistries().hubTiles`, switched-on only.

/** Today on the portal clock ("Monday, October 26"). */
const todayLabel = () => formatInstant(new Date(), { weekday: 'long', month: 'long', day: 'numeric' }, undefined);

export function DashboardPage() {
  const { canSeeTile, user } = useAuth();
  const { hubTiles } = useModuleRegistries();
  const SHELL_TILES = hubTiles.filter((t) => t.surface === 'shell');
  const OTHER_TILES = hubTiles.filter((t) => t.surface !== 'shell');
  const firstName = (user?.fullName || '').trim().split(/\s+/)[0] ?? '';
  const hour = new Date().getHours();
  const greeting =
    hour < 5 ? 'Still weaving' : hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  // HR is admin-only as a tile, but Appraisals (under HR) is reachable by
  // managers + every employee (`appraisalsAware`). Surface the HR tile when
  // the user can open Appraisals even without the full HR tile grant.
  const visible = (m: HubTile): boolean =>
    m.appraisalsAware ? canSeeTile(m.slug) || canAccessAppraisals(user) : canSeeTile(m.slug);
  const visibleModules = SHELL_TILES.filter(visible);
  // Non-admins with appraisal access land directly on /hr/appraisals (the
  // bare /hr route would 403 them via RequireTile).
  const moduleTo = (m: HubTile): string =>
    m.appraisalsAware && m.nonAdminTo && !canSeeTile(m.slug) && canAccessAppraisals(user) ? m.nonAdminTo : m.to;

  /*
   * R5 FRONTEND-LEAD pass-2 deferred the hero status counts behind a
   * placeholder "<tile-count> modules" string. Round-8 wires the right-
   * side meta row to the real backend:
   *
   *   GET /api/coportal/dashboard/summary   → issuesOpen
   *   GET /api/coportal/agents              → agents count
   *
   * Both queries are gated by `canSeeTile('cs-portal')`: a viewer who
   * doesn't have CS Portal access shouldn't see those numbers (the API
   * would 403 anyway). When the queries fail or return empty, we render
   * a quiet dash rather than a hard zero so the hero stays composed.
   */
  const csPortalVisible = canSeeTile('cs-portal');
  const summaryQuery = useQuery({
    queryKey: ['coportal', 'dashboard', 'summary'] as const,
    queryFn: ({ signal }) => fetchOpenIssues(signal!),
    staleTime: 60_000,
    retry: false,
    enabled: csPortalVisible,
  });
  const agentsQuery = useQuery({
    queryKey: ['coportal', 'agents', 'meta-count'] as const,
    queryFn: ({ signal }) => fetchAgentsCount(signal!),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: csPortalVisible,
  });

  const openIssues = summaryQuery.data;
  const agentsCount = agentsQuery.data;

  const hideKnot = useSyncExternalStore(subscribeNarrow, readNarrow, () => false);
  // Getting started (2026-10-27): only for a workspace being set up or an
  // invited colleague; null for Asian Trails' existing workspace and people.
  const checklist = useOnboardingMe().data?.checklist ?? null;

  return (
    <div>
      {/*
        VP-8 — Hub-homepage atmosphere. Fixed 64px grid + cobalt/sienna
        radial orbs, masked to the upper-centre of the viewport. Lives
        ONLY on the dashboard root (recon §8 "Hub-homepage grid"). Used
        to be Shell-mounted which painted the grid behind every module;
        scoped here so feature pages render against a clean `--bg`.
      */}
      <div className="hub-bg" aria-hidden />
      <section className="hub-hero">
        {/* signature moment — the Amassa knot woven into the hero corner */}
        <AmassaMark
          variant="mark"
          size={340}
          fill="var(--grad-signal)"
          aria-hidden
          style={{
            position: 'absolute',
            top: -70,
            right: -46,
            opacity: 0.14,
            transform: 'rotate(8deg)',
            pointerEvents: 'none',
            zIndex: 0,
            // New interface, phone width: the knot would sit under the
            // greeting, so it is not printed there.
            ...(hideKnot ? { display: 'none' } : null),
          }}
        />
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 20,
            fontFamily: 'var(--hm-mono)',
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            fontWeight: 500,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--q-info, var(--c-bd))',
              boxShadow: 'var(--q-none, 0 0 8px var(--c-bd))',
              animation: 'var(--q-none, hm-pulse 2.4s ease-in-out infinite)',
            }}
          />
          amassa · live · {todayLabel()}
        </div>
        <h1 className="hub-hero-title">
          {greeting}
          {firstName ? `, ${firstName}` : ''}.
        </h1>
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            marginTop: 22,
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            fontSize: 13,
            color: 'var(--text-muted)',
            flexWrap: 'wrap',
          }}
        >
          <span>
            <strong style={{ color: 'var(--text)', fontWeight: 500 }}>
              {visibleModules.length}
            </strong>{' '}
            modules
          </span>
          {csPortalVisible && (
            <>
              <Divider />
              <span>
                <strong style={{ color: 'var(--text)', fontWeight: 500 }}>
                  {openIssues != null ? openIssues : '—'}
                </strong>{' '}
                open issues
              </span>
              <Divider />
              <span>
                <strong style={{ color: 'var(--text)', fontWeight: 500 }}>
                  {agentsCount != null ? agentsCount : '—'}
                </strong>{' '}
                agents
              </span>
            </>
          )}
          <Divider />
          <span>
            <strong style={{ color: 'var(--text)', fontWeight: 500 }}>Amassa</strong>{' '}
            by quotinc
          </span>
        </div>
      </section>

      {checklist && <GettingStartedCard checklist={checklist} />}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--hm-mono)',
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            fontWeight: 500,
          }}
        >
          modules
        </span>
        <span
          style={{
            fontFamily: 'var(--hm-mono)',
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--text-dim)',
            fontWeight: 500,
          }}
        >
          {visibleModules.length} tiles
        </span>
      </div>

      <div
        className="motion-stagger"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {visibleModules.map((m) => (
          <Tile
            key={m.to}
            to={moduleTo(m)}
            slug={m.slug}
            label={m.label}
            accent={m.accent}
            mod={m.mod}
            hint={m.hint}
          />
        ))}
        {OTHER_TILES.filter(visible).map((m) => {
          const Glyph = iconForKey(m.icon);
          return (
            <Tile
              key={m.to}
              to={moduleTo(m)}
              slug={m.slug}
              label={m.label}
              accent={m.accent}
              hint={m.hint}
              icon={<Glyph size={22} />}
            />
          );
        })}
        {/* Admins add and remove modules "from Hub" (brief §5.2): a quiet
            last tile into Admin ▸ Modules' gallery. */}
        {user?.role === 'admin' && (
          <Link to="/admin/modules?add=1" style={addTile} data-testid="hub-add-module">
            <span style={addTileMark} aria-hidden>
              +
            </span>
            <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>Add module</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>From a template, or a board for a team</span>
          </Link>
        )}
      </div>
    </div>
  );
}

const addTile: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 4,
  minHeight: 120,
  padding: '18px 20px',
  borderRadius: 'var(--hm-radius-lg)',
  border: '1px dashed var(--border-strong)',
  background: 'transparent',
  textDecoration: 'none',
  color: 'var(--text-muted)',
};

const addTileMark: CSSProperties = {
  width: 30,
  height: 30,
  display: 'grid',
  placeItems: 'center',
  marginBottom: 6,
  borderRadius: 'var(--q-r-md)',
  border: '1px solid var(--border)',
  fontSize: 18,
  lineHeight: 1,
  color: 'var(--brand)',
};

function Divider() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 1,
        height: 12,
        background: 'var(--border-strong)',
      }}
    />
  );
}
